import { Suspense, lazy } from 'react';
import { Navigate } from 'react-router-dom';
import SidebarLayout from 'src/layouts/SidebarLayout';
import BaseLayout from 'src/layouts/BaseLayout';
import SuspenseLoader from 'src/components/SuspenseLoader';
import AssistantRestrictedRoute from 'src/components/AssistantRestrictedRoute';

const Loader = (Component) => (props) =>
  (
    <Suspense fallback={<SuspenseLoader />}>
      <Component {...props} />
    </Suspense>
  );

// Pages

const Overview = Loader(lazy(() => import('src/content/overview')));

// Dashboards

const Tasks = Loader(lazy(() => import('src/content/dashboards/Tasks')));

// Applications


const Services = Loader(
  lazy(() => import('src/content/applications/Services'))
);
const BusinessArea = Loader(
  lazy(() => import('src/content/applications/BusinessArea'))
);
const EvaluationCriteria = Loader(
  lazy(() => import('src/content/applications/EvaluationCriteria'))
);
const CompanySettings = Loader(
  lazy(() => import('src/content/applications/CompanySettings'))
);
const GeminiConfig = Loader(
  lazy(() => import('src/content/applications/GeminiConfig'))
);
const Countries = Loader(
  lazy(() => import('src/content/applications/Countries'))
);
const Companies = Loader(
  lazy(() => import('src/content/applications/Companies'))
);
const UserProfile = Loader(
  lazy(() => import('src/content/applications/Users/profile'))
);

const UserSettings = Loader(
  lazy(() => import('src/content/applications/Users/settings'))
);
const Assistants = Loader(
  lazy(() => import('src/content/applications/Assistants'))
);
const ActivityLog = Loader(
  lazy(() => import('src/content/applications/ActivityLog'))
);
const CardPayments = Loader(
  lazy(() => import('src/content/applications/CardPayments'))
);
const IndexCustomers = Loader(
  lazy(() => import('./content/applications/Customers/IndexCustomers'))
);
const IndexStaffDirectory = Loader(
  lazy(() => import('./content/applications/StaffDirectory/IndexStaffDirectory'))
);
const SubscriptionIndex = Loader(
  lazy(() => import('./content/applications/subscriptions'))
);
const SubscriptionTable = Loader(
  lazy(() => import('./content/applications/subscriptions/SubscriptionTable'))
);

// Components

const CompaniesTable = Loader(
  lazy(() => import('src/content/applications/Companies/CompaniesTable'))
);

const CompanyDetails = Loader(
  lazy(() => import('src/content/applications/Companies/details'))
);
const Suggestions = Loader(
  lazy(() => import('src/content/applications/Suggestions'))
);
const ContactMessages = Loader(
  lazy(() => import('src/content/applications/ContactMessages'))
);
const ForgetPassword = Loader(
  lazy(() => import('src/content/ForgetPassword/ForgotPassword'))
);
const ResetPassword = Loader(
  lazy(() => import('src/content/ForgetPassword/ResetPassword'))
);
// Status

const Status404 = Loader(
  lazy(() => import('src/content/pages/Status/Status404'))
);
const Status500 = Loader(
  lazy(() => import('src/content/pages/Status/Status500'))
);
const StatusComingSoon = Loader(
  lazy(() => import('src/content/pages/Status/ComingSoon'))
);
const StatusMaintenance = Loader(
  lazy(() => import('src/content/pages/Status/Maintenance'))
);

const routes = [
  {
    path: '',
    element: <BaseLayout />,
    children: [
      {
        path: '/',
        element: <Overview />
      },
      {
        path: 'overview',
        element: <Navigate to="/" replace />
      },
      {
        path: 'forgetPassword',
        element: <ForgetPassword />
      },
      {
        path: 'resetpassword',
        element: <ResetPassword />
      },
      {
        path: 'status',
        children: [
          {
            path: '',
            element: <Navigate to="404" replace />
          },
          {
            path: '404',
            element: <Status404 />
          },
          {
            path: '500',
            element: <Status500 />
          },
          {
            path: 'maintenance',
            element: <StatusMaintenance />
          },
          {
            path: 'coming-soon',
            element: <StatusComingSoon />
          }
        ]
      },
      {
        path: '*',
        element: <Status404 />
      }
    ]
  },
  {
    path: 'dashboards',
    element: <SidebarLayout />,
    children: [
      {
        path: '',
        element: <Navigate to="tasks" replace />
      },
      {
        path: 'tasks',
        element: (
          <AssistantRestrictedRoute>
            <Tasks />
          </AssistantRestrictedRoute>
        )
      }
    ]
  },
  {
    path: 'management',
    element: <SidebarLayout />,
    children: [
      {
        path: '',
        element: <Navigate to="profile" replace />
      },
      {
        path: 'services',
        element: (
          <AssistantRestrictedRoute>
            <Services />
          </AssistantRestrictedRoute>
        )
      },
      {
        path: 'suggestions',
        element: (
          <AssistantRestrictedRoute>
            <Suggestions />
          </AssistantRestrictedRoute>
        )
      },
      {
        path: 'contact-messages',
        element: (
          <AssistantRestrictedRoute>
            <ContactMessages />
          </AssistantRestrictedRoute>
        )
      },
      {
        path: 'BusinessArea',
        element: (
          <AssistantRestrictedRoute>
            <BusinessArea />
          </AssistantRestrictedRoute>
        )
      },
      {
        path: 'evaluation-criteria',
        element: (
          <AssistantRestrictedRoute>
            <EvaluationCriteria />
          </AssistantRestrictedRoute>
        )
      },
      {
        // Société CliClaim (ancien onglet de profile/settings) : réservée au vrai SUPER_ADMIN.
        path: 'company-settings',
        element: (
          <AssistantRestrictedRoute>
            <CompanySettings />
          </AssistantRestrictedRoute>
        )
      },
      {
        // Configuration Gemini (prompts + périodes de génération) : réservée au vrai SUPER_ADMIN.
        path: 'ai-prompts',
        element: (
          <AssistantRestrictedRoute>
            <GeminiConfig />
          </AssistantRestrictedRoute>
        )
      },
      {
        path: 'countries',
        element: (
          <AssistantRestrictedRoute>
            <Countries />
          </AssistantRestrictedRoute>
        )
      },
      {
        // Entreprises : seule partie de GESTION accessible à un compte assistant.
        path: 'companies',
        element: <Companies />,
        children: [
          {
            path: '',
            element: <CompaniesTable />
          },

          {
            path: 'details/:id',
            element: <CompanyDetails />
          }
        ]
      },
      {
        path: 'subscriptions',
        element: (
          <AssistantRestrictedRoute>
            <SubscriptionIndex />
          </AssistantRestrictedRoute>
        ),
        children: [
          {
            path: '',
            element: <SubscriptionTable />
          }
        ]
      },
      {
        path: 'clients',
        element: (
          <AssistantRestrictedRoute>
            <IndexCustomers />
          </AssistantRestrictedRoute>
        )
      },
      {
        path: 'staff',
        element: (
          <AssistantRestrictedRoute>
            <IndexStaffDirectory />
          </AssistantRestrictedRoute>
        )
      },
      {
        // Comptes assistants : gestion réservée au vrai SUPER_ADMIN.
        path: 'assistants',
        element: (
          <AssistantRestrictedRoute>
            <Assistants />
          </AssistantRestrictedRoute>
        )
      },
      {
        // Journal d'activité : réservé au vrai SUPER_ADMIN (un assistant peut y apparaître
        // comme acteur, mais ne peut jamais le consulter).
        path: 'activity-log',
        element: (
          <AssistantRestrictedRoute>
            <ActivityLog />
          </AssistantRestrictedRoute>
        )
      },
      {
        // Paiements par carte (Moyasar) : consultation seule, réservée au vrai SUPER_ADMIN pour
        // l'instant, comme la page Abonnements.
        path: 'card-payments',
        element: (
          <AssistantRestrictedRoute>
            <CardPayments />
          </AssistantRestrictedRoute>
        )
      },

      {
        path: 'profile',
        children: [
          {
            path: '',
            element: <Navigate to="details" replace />
          },
          {
            path: 'details',
            element: <UserProfile />
          },
          {
            path: 'settings',
            element: <UserSettings />
          }
        ]
      }
    ]
  }
];

export default routes;
